import React, { useState } from "react";
import {
  Box,
  Image,
  Text,
  Badge,
  VStack,
  HStack,
  Tooltip,
  useBreakpointValue,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import parse from 'html-react-parser';

const ServiceCard = ({ service, isSelected, handleSelect, isLoading }) => {
  const { t, i18n } = useTranslation();
  const {
    name,
    name_en,
    name_fr,
    description,
    description_en,
    description_fr,
    is_active,
    priority,
    tags,
    price_starting_from,
    sessions_conducted,
  } = service;

  const currentLanguage = i18n.language;

  const activeBadgeColor = is_active ? "green" : "red";
  const priorityLabel = priority > 0 ? "High Priority" : "Standard";

  // Responsive styles for spacing, font size, and layout
  const cardPadding = useBreakpointValue({ base: "0.8rem", md: "1rem" });
  const imageHeight = useBreakpointValue({ base: "140px", md: "180px" });
  const fontSize = useBreakpointValue({ base: "sm", md: "lg" });
  const badgeSize = useBreakpointValue({ base: "xs", md: "sm" });
  const hashtagFontSize = useBreakpointValue({ base: "xs", md: "sm" });
  const badgePadding = useBreakpointValue({ base: "0 4px", md: "2px 6px" });

  // State to manage image load status
  const [isImageLoaded, setImageLoaded] = useState(false);

  // console.log("service", service)

  return (
    // <Tooltip
    //   label={currentLanguage === "fr" ? description_fr : description}
    //   aria-label="Full description"
    //   hasArrow
    //   placement="top"
    //   bg="gray.700"
    //   color="white"
    //   maxW="300px"
    //   overflowWrap="break-word"
    // >
    <Box
      as={motion.div}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      border="1px solid"
      borderColor={isSelected ? "#FF6666" : "gray.300"}
      borderRadius="md"
      padding={cardPadding}
      marginBottom="1.2rem"
      marginX="5px"
      boxShadow="lg"
      cursor="pointer"
      onClick={() => handleSelect(service?.id)}
      width="100%"
      minH={{ base: "400px", md: "550px" }}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      position="relative"
      overflow="hidden"
    >
      {/* Image Skeleton */}
      <Skeleton isLoaded={isImageLoaded} borderRadius="md">
        <Image
          src={service?.image}
          borderRadius="md"
          fit="cover"
          width="100%"
          height={imageHeight}
          alt={name}
          onLoad={() => setImageLoaded(true)} // Set image load state
          marginBottom="0.4rem"
        />
      </Skeleton>

      {/* Service Details */}
      <VStack spacing={1} alignItems="center" marginBottom="0.5rem">
        {isLoading ? (
          <SkeletonText noOfLines={1} skeletonHeight="20px" width="80%" />
        ) : (
          <Text
            fontSize={fontSize}
            color="#FF6666"
            fontWeight="bold"
            noOfLines={2}
            textAlign="center"
          >
            {currentLanguage === "fr" ? name_fr : name_en}
          </Text>
        )}


        {/* Price Starting From */}
        {price_starting_from && (
          <Text fontSize={fontSize} color="gray.600">
            {t("Starting_from")}: {price_starting_from}
          </Text>
        )}
      </VStack>

      {/* Truncated Description */}
      <Box textAlign="center" marginBottom="0.3rem">
        {isLoading ? (
          <SkeletonText noOfLines={2} spacing="2" skeletonHeight="15px" />
        ) : (
          <Box color="gray.600">
            {parse(currentLanguage === "fr" ? description_fr : description_en)}
          </Box>
        )}
      </Box>

      {/* Hashtag Badges */}
      <HStack
        wrap="wrap"
        whiteSpace="nowrap"
        justify="center"
        spacing={2}
        rowGap={1}
        marginTop="0.3rem"
        marginBottom="1rem"
      >
        {tags.map((tag, index) => (
          <Badge
            key={index}
            colorScheme="blue"
            fontSize={hashtagFontSize}
            padding={badgePadding}
            borderRadius="full"
          >
            {tag}
          </Badge>
        ))}
      </HStack>

      {/* Session Counter Strip */}
      {sessions_conducted > 100 && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          bg="teal.500"
          color="white"
          textAlign="center"
          fontSize={fontSize}
          padding="0.02rem"
          borderBottomRadius="md"
        >
          {t("Sessions Conducted")}: {sessions_conducted}
        </Box>
      )}
    </Box>
    // </Tooltip>
  );
};

export default ServiceCard;
